import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigation } from 'utils/context/NavigationContext';

interface UseStateWithOptions<T> {
  id: string;
  initialState: T;
  onBack?: (
    state: T,
    options?: {
      decrement?: number;
      customBoolean?: boolean;
      customString?: string;
    },
  ) => T;
  decrement?: number;
  customBoolean?: boolean;
  customString?: string;
}

function useGeneralState<T>({
  id,
  initialState,
  onBack = defaultBackAction,
  decrement = 1,
  customBoolean = false,
  customString = '',
}: UseStateWithOptions<T>) {
  const {
    pushToStack,
    popFromStack,
    updateStack,
    deleteFromStack,
    removeFromStack,
    navigationStack,
  } = useNavigation();
  const [state, setStateInternal] = useState<T>(initialState);

  /*
   * `setState` must keep a stable identity: consumers put it in effect
   * dependency arrays (e.g. `setShowSection` in InputBox), and re-creating it on
   * every navigation-stack change re-ran those effects at arbitrary moments —
   * which is how the composer stole focus from a just-opened row menu and Radix
   * dismissed it. Reading the stack and the current state from refs keeps the
   * callback free of changing dependencies.
   *
   * The refs are read ONCE per call, not inside the onBack closures: an entry's
   * job is to undo the change that created it, so it has to remember the value
   * it replaced. Consulting the live ref when Back is pressed would write the
   * current value straight back and leave the UI where it was.
   */
  const stackRef = useRef(navigationStack);
  useEffect(() => {
    stackRef.current = navigationStack;
  }, [navigationStack]);

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Enhanced setState that manages the navigation stack
  const setState = useCallback(
    (
      newState: T,
      operation: 'Update' | 'Push' | 'Pop' | 'Delete' | 'Remove' = 'Update',
      customOnBack?: (currentState: T) => T,
    ) => {
      const navigationStackNow = stackRef.current;
      // The value this call is about to replace — what Back must restore.
      const stateBeforeChange = stateRef.current;
      const existingIndex = navigationStackNow.findIndex(
        item => item.id === id,
      );
      const shouldUpdate = existingIndex !== -1 && operation === 'Update';
      if (shouldUpdate) {
        updateStack(id, {
          ...navigationStackNow[existingIndex],
          onBack: () => {
            const updatedState = customOnBack
              ? customOnBack(stateBeforeChange)
              : onBack(stateBeforeChange, {
                  decrement,
                  customBoolean,
                  customString,
                });
            setStateInternal(updatedState);
          },
        });
      } else if (
        operation === 'Pop' &&
        navigationStackNow.length &&
        id === navigationStackNow.slice(-1)[0].id
      ) {
        popFromStack();
      } else if (operation === 'Delete') {
        deleteFromStack(id);
      } else if (operation === 'Remove') {
        // Remove from stack without triggering onBack callback (for programmatic closes)
        removeFromStack(id);
      } else {
        // Push new item to stack, handle back action differently based on forcePush
        pushToStack({
          id,
          onBack: () => {
            if (customOnBack) {
              const revertedState = customOnBack(stateBeforeChange);
              setStateInternal(revertedState);
            } else if (existingIndex !== -1) {
              // revert to the previous state without custom logic
              setStateInternal(stateBeforeChange);
            } else {
              // Normal back action
              const updatedState = onBack(stateBeforeChange, {
                decrement,
                customBoolean,
                customString,
              });
              setStateInternal(updatedState);
            }
          },
        });
      }
      setStateInternal(newState);
    },
    [
      id,
      updateStack,
      onBack,
      decrement,
      customBoolean,
      customString,
      popFromStack,
      deleteFromStack,
      removeFromStack,
      pushToStack,
    ],
  );

  const close = useCallback(() => {
    popFromStack();
  }, [popFromStack]);

  return { state, setState, close };
}

function defaultBackAction<T>(
  state: T,
  options: {
    decrement?: number;
    customBoolean?: boolean;
    customString?: string;
  } = {},
): T {
  const { decrement = 1, customBoolean = false, customString = '' } = options;

  if (typeof state === 'number') {
    return Math.max(0, state - decrement) as T;
  } else if (typeof state === 'boolean') {
    return customBoolean as unknown as T;
  } else if (typeof state === 'string') {
    return customString as unknown as T;
  }
  return state;
}

export default useGeneralState;
